import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';

const SWITCH_OPERATION_DURATION = 30; // seconds

/**
 * SwitchProgressBanner
 * Shown after a switch ON/OFF command is sent.
 * Counts up over SWITCH_OPERATION_DURATION seconds then calls onComplete.
 */
const SwitchProgressBanner = ({ actionType, onComplete }) => {
    const [elapsed, setElapsed] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            setElapsed(prev => {
                if (prev + 1 >= SWITCH_OPERATION_DURATION) {
                    clearInterval(intervalRef.current);
                    onComplete();
                    return SWITCH_OPERATION_DURATION;
                }
                return prev + 1;
            });
        }, 1000);
        return () => clearInterval(intervalRef.current);
    }, []);

    const pct = Math.round((elapsed / SWITCH_OPERATION_DURATION) * 100);
    const remaining = SWITCH_OPERATION_DURATION - elapsed;
    const isOff = actionType === 'switch_off';

    const barColor    = isOff ? '#dc3545' : '#198754';
    const bgColor     = isOff ? '#fff5f5' : '#f0fff4';
    const borderColor = isOff ? '#f5c6cb' : '#c3e6cb';
    const textColor   = isOff ? '#842029' : '#0f5132';
    const iconClass   = isOff ? 'ti ti-plug-off' : 'ti ti-plug';

    return (
        <div
            style={{
                background: bgColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '8px',
                padding: '14px 18px',
                marginBottom: '16px',
            }}
        >
            {/* Header row */}
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                    <i className={iconClass} style={{ color: barColor, fontSize: '1.1rem' }} />
                    <strong style={{ color: textColor, fontSize: '0.92rem' }}>
                        {isOff ? 'Switching meter OFF…' : 'Switching meter ON…'}
                    </strong>
                </div>
                <span style={{ fontSize: '0.82rem', color: textColor, fontVariantNumeric: 'tabular-nums' }}>
                    {remaining}s remaining
                </span>
            </div>

            {/* Progress bar */}
            <div style={{ height: '8px', borderRadius: '4px', background: '#e9ecef', overflow: 'hidden' }}>
                <div
                    style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: barColor,
                        borderRadius: '4px',
                        transition: 'width 0.9s linear',
                    }}
                />
            </div>

            {/* Sub-text */}
            <p style={{ fontSize: '0.78rem', color: textColor, margin: '6px 0 0', opacity: 0.75 }}>
                Please wait. The meter is processing your request. The page will refresh automatically.
            </p>
        </div>
    );
};

const MeterInfoTab = () => {
    const { meterId } = useParams();
    const [meterData, setMeterData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState('');
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    // Switch operation progress state
    const [switchOperationActive, setSwitchOperationActive] = useState(false);
    const [switchOperationType, setSwitchOperationType] = useState('');

    useEffect(() => {
        if (meterId && selectedFacilityId) {
            fetchMeterDetails();
        }
    }, [meterId, selectedFacilityId]);

    const fetchMeterDetails = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `/api/app/power_meter_management/controllers/power_meters/get_meter/${meterId}`,
                'GET'
            );
            if (response && response.success) {
                setMeterData(response.data.data);
            } else {
                toastify(response?.error || 'Failed to fetch meter details', 'error');
            }
        } catch (error) {
            console.error('Error fetching meter details:', error);
            toastify('An error occurred while fetching meter details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchOn = () => {
        setConfirmAction('switch_on');
        setConfirmMessage('Are you sure you want to switch ON this meter?');
        setShowConfirmDialog(true);
    };

    const handleSwitchOff = () => {
        setConfirmAction('switch_off');
        setConfirmMessage('Are you sure you want to switch OFF this meter?');
        setShowConfirmDialog(true);
    };

    const handleEnforcement = () => {
        const action = meterData?.enforcement ? 'remove_enforcement' : 'add_enforcement';
        setConfirmAction(action);
        setConfirmMessage(
            meterData?.enforcement
                ? 'Are you sure you want to REMOVE this meter from enforcement?'
                : 'Are you sure you want to ADD this meter to enforcement?'
        );
        setShowConfirmDialog(true);
    };

    const executeAction = async () => {
        if (!confirmAction || !meterId) return;

        try {
            setActionLoading(true);
            let endpoint = '';
            const method = 'POST';
            let successMessage = '';

            switch (confirmAction) {
                case 'switch_on':
                    endpoint = `/api/app/power_meter_management/controllers/commands/switch_on/${meterId}`;
                    successMessage = 'Switch ON command sent. Processing…';
                    break;
                case 'switch_off':
                    endpoint = `/api/app/power_meter_management/controllers/commands/switch_off/${meterId}`;
                    successMessage = 'Switch OFF command sent. Processing…';
                    break;
                case 'add_enforcement':
                    endpoint = `/api/app/power_meter_management/controllers/power_meters/add_enforcement/${meterId}`;
                    successMessage = 'Meter added to enforcement successfully';
                    break;
                case 'remove_enforcement':
                    endpoint = `/api/app/power_meter_management/controllers/power_meters/remove_enforcement/${meterId}`;
                    successMessage = 'Meter removed from enforcement successfully';
                    break;
                default:
                    return;
            }

            const response = await makeRequest2(endpoint, method);

            if (response && (response.success || response.message)) {
                toastify(successMessage, 'success');

                // Only show the 30-second banner for switch on/off
                if (confirmAction === 'switch_on' || confirmAction === 'switch_off') {
                    setSwitchOperationType(confirmAction);
                    setSwitchOperationActive(true);
                } else {
                    // Enforcement changes don't need the delay
                    fetchMeterDetails();
                }
            } else {
                toastify(response?.error || 'Action failed', 'error');
            }
        } catch (error) {
            console.error('Error executing action:', error);
            toastify('An error occurred while executing the action', 'error');
        } finally {
            setActionLoading(false);
            setShowConfirmDialog(false);
            setConfirmAction(null);
        }
    };

    // Called when the 30-second countdown finishes
    const handleSwitchOperationComplete = () => {
        setSwitchOperationActive(false);
        setSwitchOperationType('');
        fetchMeterDetails();
        toastify('Switch operation complete. Status refreshed.', 'success');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'on':
            case 'active':
                return 'bg-success';
            case 'off':
                return 'bg-danger';
            case 'maintenance':
                return 'bg-warning';
            default:
                return 'bg-secondary';
        }
    };

    const renderActionButtons = () => {
        if (!meterData) return null;

        const isOn = meterData.liveStatus?.toLowerCase() === 'on';
        const isEnforced = meterData.enforcement;

        return (
            <div className="d-flex flex-wrap gap-2 mt-3">
                {/* Switch ON — only if off, not enforced, and no operation in progress */}
                {!isOn && !isEnforced && (
                    <Button
                        label="Switch ON"
                        icon="ti ti-power"
                        className="btn btn-primary btn-sm"
                        onClick={handleSwitchOn}
                        disabled={actionLoading || switchOperationActive}
                    />
                )}

                {/* Switch OFF — only if on and no operation in progress */}
                {isOn && (
                    <Button
                        label="Switch OFF"
                        icon="ti ti-power"
                        className="btn btn-danger btn-sm"
                        onClick={handleSwitchOff}
                        disabled={actionLoading || switchOperationActive}
                    />
                )}

                {/* Enforcement — always visible but locked during switch operation */}
                <Button
                    label={isEnforced ? "Remove Enforcement" : "Add Enforcement"}
                    icon={isEnforced ? "ti ti-shield-off" : "ti ti-shield"}
                    className={`p-button-sm ${isEnforced ? 'p-button-warning' : 'p-button-info'}`}
                    onClick={handleEnforcement}
                    disabled={actionLoading || switchOperationActive}
                />
            </div>
        );
    };

    if (loading) {
        return (
            <div className="tab-pane show active" role="tabpanel">
                <div className="text-center py-5">
                    <i className="ti ti-loader fa-spin fa-2x"></i>
                    <p className="mt-2">Loading meter details...</p>
                </div>
            </div>
        );
    }

    if (!meterData) {
        return (
            <div className="tab-pane show active" role="tabpanel">
                <div className="text-center py-5">
                    <i className="ti ti-exclamation-triangle fa-2x text-warning"></i>
                    <p className="mt-2">Meter details not found</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="tab-pane show active" role="tabpanel">

                {/* ---- 30-second progress banner (shown outside the card grid so it's always visible) ---- */}
                {switchOperationActive && (
                    <SwitchProgressBanner
                        actionType={switchOperationType}
                        onComplete={handleSwitchOperationComplete}
                    />
                )}

                <div className="row">
                    <div className="col-lg-4 col-xxl-3">
                        <div className="card">
                            <div className="card-body position-relative">
                                <div className="position-absolute end-0 top-0 p-3">
                                    <span className={`badge ${getStatusBadgeClass(meterData.liveStatus)}`}>
                                        {meterData.liveStatus || 'Off'}
                                    </span>
                                    {meterData.enforcement && (
                                        <span className="badge bg-warning ms-1">Enforced</span>
                                    )}
                                </div>
                                <div className="text-center mt-4">
                                    <h1 className="mb-0">
                                        {meterData.currentMeterReading || 0} kWh
                                    </h1>
                                    <p className="text-muted text-sm">
                                        Meter S/N: {meterData.meterSerialNumber}
                                    </p>
                                    <hr className="my-3 border border-secondary-subtle" />
                                    <div className="d-inline-flex align-items-center justify-content-start w-100 mb-3 text-center">
                                        <p className="mb-0">
                                            Updated: {formatDate(meterData.lastUpdated)}
                                        </p>
                                    </div>
                                    {renderActionButtons()}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-8 col-xxl-9">
                        <div className="card">
                            <div className="card-header">
                                <h5>Meter Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <strong>Meter Type:</strong> {meterData.type || '-'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Manufacturer:</strong> {meterData.manufacturer || '-'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Device ID:</strong> {meterData.deviceId || '-'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Gateway ID:</strong> {meterData.gatewayId || '-'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Assigned Unit:</strong> {meterData.unitName || 'Not Assigned'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Meter Status:</strong>
                                        <span className={`badge ms-2 ${getStatusBadgeClass(meterData.meterStatus)}`}>
                                            {meterData.meterStatus || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Communication Status:</strong>
                                        <span className={`badge ms-2 ${meterData.communicationStatus ? 'bg-success' : 'bg-danger'}`}>
                                            {meterData.communicationStatus ? 'Online' : 'Offline'}
                                        </span>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Gateway Live Status:</strong> {meterData.gatewayLiveStatus || '-'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Previous Reading:</strong> {meterData.previousMeterReading || 0} kWh
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Current Reading:</strong> {meterData.currentMeterReading || 0} kWh
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Power Factor (PF):</strong> {meterData.pf || '-'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Watt Hours (Wh):</strong> {meterData.wh || '-'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Created At:</strong> {formatDate(meterData.createdAt)}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Updated At:</strong> {formatDate(meterData.updatedAt)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog */}
            <Dialog
                header="Confirm Action"
                visible={showConfirmDialog}
                onHide={() => setShowConfirmDialog(false)}
                style={{ width: '400px' }}
            >
                <div className="confirmation-content">
                    <div className="flex align-items-center justify-content-center mb-3">
                        <i className="ti ti-exclamation-circle fa-2x text-warning"></i>
                    </div>
                    <p className="text-center">{confirmMessage}</p>
                    {(confirmAction === 'switch_on' || confirmAction === 'switch_off')}
                </div>
                <div className="text-end mt-4">
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={() => setShowConfirmDialog(false)}
                        className="p-button-text mr-2"
                        disabled={actionLoading}
                    />
                    <Button
                        label={actionLoading ? "Processing..." : "Confirm"}
                        icon="pi pi-check"
                        onClick={executeAction}
                        disabled={actionLoading}
                        className="p-button-primary"
                    />
                </div>
            </Dialog>
        </>
    );
};

export default MeterInfoTab;